import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Form,
  Spinner,
  Table,
} from 'react-bootstrap';
import { FaList, FaSave, FaSearch, FaSync, FaTimes } from 'react-icons/fa';
import AiSchemaApi from '../../../../api/AiSchemaApi';
import AiSchemaFieldNotesModal from './AiSchemaFieldNotesModal';

function buildDraftsFromCatalog(rows) {
  const nextDrafts = {};
  for (const row of rows) {
    nextDrafts[row.object_name] = {
      description: row.description || '',
      included: row.included === true,
      field_notes: row.field_notes && typeof row.field_notes === 'object' ? { ...row.field_notes } : {},
    };
  }
  return nextDrafts;
}

function isRowDirty(row, draft) {
  if (!draft) return false;
  const savedNotes = row.field_notes && typeof row.field_notes === 'object' ? row.field_notes : {};
  const draftNotes = draft.field_notes && typeof draft.field_notes === 'object' ? draft.field_notes : {};
  return (
    draft.included !== (row.included === true)
    || String(draft.description || '').trim() !== String(row.description || '').trim()
    || JSON.stringify(draftNotes) !== JSON.stringify(savedNotes)
  );
}

function matchesSearch(row, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.object_name,
    row.object_label,
    row.description,
    row.default_description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

const AiSchemaCatalogSection = ({
  mode = 'custom',
  disabled = false,
  onCatalogStateChange,
}) => {
  const isStandard = mode === 'standard';
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ variant: '', text: '' });
  const [drafts, setDrafts] = useState({});
  const [fieldModalObject, setFieldModalObject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadCatalog = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = isStandard
        ? await AiSchemaApi.listStandardCatalog()
        : await AiSchemaApi.listCatalog();
      if (!res?.success) throw new Error(res?.message || 'Failed to load schema catalog.');
      const rows = Array.isArray(res.data) ? res.data : [];
      setCatalog(rows);
      setDrafts(buildDraftsFromCatalog(rows));
      const savedIncludedCount = rows.filter((r) => r.included).length;
      onCatalogStateChange?.({
        savedIncludedCount,
        draftIncludedCount: savedIncludedCount,
        isDirty: false,
      });
    } catch (err) {
      setMessage({ variant: 'danger', text: err.message || 'Failed to load schema catalog.' });
      setCatalog([]);
      setDrafts({});
      onCatalogStateChange?.({ savedIncludedCount: 0, draftIncludedCount: 0, isDirty: false });
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [isStandard, onCatalogStateChange]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const draftIncludedCount = useMemo(
    () => Object.values(drafts).filter((d) => d?.included === true).length,
    [drafts]
  );

  const isDirty = useMemo(
    () => catalog.some((row) => isRowDirty(row, drafts[row.object_name])),
    [catalog, drafts]
  );

  const savedIncludedCount = useMemo(
    () => catalog.filter((r) => r.included).length,
    [catalog]
  );

  const filteredCatalog = useMemo(
    () => catalog.filter((row) => matchesSearch(row, searchQuery)),
    [catalog, searchQuery]
  );

  const hasSearch = String(searchQuery || '').trim().length > 0;

  useEffect(() => {
    onCatalogStateChange?.({
      savedIncludedCount,
      draftIncludedCount,
      isDirty,
    });
  }, [savedIncludedCount, draftIncludedCount, isDirty, onCatalogStateChange]);

  const handleIncludeToggle = (row, checked) => {
    setDrafts((prev) => ({
      ...prev,
      [row.object_name]: {
        ...prev[row.object_name],
        included: checked,
        description:
          prev[row.object_name]?.description
          || row.description
          || row.default_description
          || '',
        field_notes: prev[row.object_name]?.field_notes || row.field_notes || {},
      },
    }));
  };

  const handleDescriptionChange = (objectName, value) => {
    setDrafts((prev) => ({
      ...prev,
      [objectName]: {
        ...prev[objectName],
        description: value,
      },
    }));
  };

  const openFieldModal = (row) => {
    const draft = drafts[row.object_name] || {};
    setFieldModalObject({
      ...row,
      fields: (row.fields || []).map((field) => ({
        ...field,
        note: draft.field_notes?.[field.field_name] || field.note || '',
      })),
    });
  };

  const handleFieldNotesSave = (notes) => {
    if (!fieldModalObject) return;
    setDrafts((prev) => ({
      ...prev,
      [fieldModalObject.object_name]: {
        ...prev[fieldModalObject.object_name],
        field_notes: notes,
      },
    }));
    setFieldModalObject(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ variant: '', text: '' });
    try {
      const items = catalog.map((row) => {
        const draft = drafts[row.object_name] || {};
        return {
          object_name: row.object_name,
          included: draft.included === true,
          description: draft.description ?? '',
          field_notes: draft.field_notes || {},
        };
      });
      const res = isStandard
        ? await AiSchemaApi.saveStandardCatalogBatch(items)
        : await AiSchemaApi.saveCatalogBatch(items);
      if (!res?.success) throw new Error(res?.message || 'Save failed.');
      await loadCatalog({ silent: true });
      setMessage({
        variant: 'success',
        text: isStandard
          ? 'Standard schema catalog saved.'
          : 'Custom schema catalog saved.',
      });
    } catch (err) {
      setMessage({ variant: 'danger', text: err.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading schema catalog…
      </div>
    );
  }

  const title = isStandard ? 'Standard schema catalog' : 'Custom schema catalog';

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
        <div>
          <h6 className="mb-1">{title}</h6>
          <p className="text-muted small mb-0">
            Check objects to include. Changes apply when you click Save.
            {draftIncludedCount > 0
              ? ` ${draftIncludedCount} selected.`
              : ' None selected yet.'}
            {isDirty ? ' Unsaved changes.' : ''}
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={disabled || refreshing || saving}
            onClick={() => loadCatalog({ silent: true })}
          >
            <FaSync className="me-1" />
            Refresh
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={disabled || saving || !isDirty}
            onClick={handleSave}
            className="ai-dictionary-save-btn"
          >
            {saving ? (
              <span className="d-inline-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                Saving…
              </span>
            ) : (
              <>
                <FaSave className="me-1" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>

      {message.text && (
        <Alert
          variant={message.variant || 'info'}
          dismissible
          onClose={() => setMessage({ variant: '', text: '' })}
          className="py-2 small"
        >
          {message.text}
        </Alert>
      )}

      {catalog.length === 0 ? (
        <Alert variant="light" className="border small mb-0">
          {isStandard
            ? 'No standard objects found in the catalog.'
            : 'No custom objects or custom fields on standard objects found in Object Manager.'}
        </Alert>
      ) : (
        <div className="position-relative">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <div className="ai-schema-catalog-search flex-grow-1">
              <FaSearch className="ai-schema-catalog-search-icon text-muted" aria-hidden />
              <Form.Control
                type="search"
                size="sm"
                placeholder="Search by object name or label…"
                value={searchQuery}
                disabled={disabled || saving}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search objects"
              />
              {hasSearch && (
                <Button
                  variant="link"
                  size="sm"
                  className="ai-schema-catalog-search-clear text-muted"
                  disabled={disabled || saving}
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <FaTimes />
                </Button>
              )}
            </div>
            <span className="text-muted small">
              {hasSearch
                ? `${filteredCatalog.length} of ${catalog.length} shown`
                : `${catalog.length} object${catalog.length === 1 ? '' : 's'}`}
            </span>
          </div>

          {(refreshing || saving) && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
              style={{ zIndex: 2 }}
            >
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="small text-muted">{saving ? 'Saving…' : 'Updating…'}</span>
            </div>
          )}
          <Table responsive bordered hover size="sm" className="bg-white mb-0 ai-schema-catalog-table">
            <thead>
              <tr>
                <th style={{ width: 56 }}>Include</th>
                <th>Object</th>
                <th>Description</th>
                <th style={{ width: 72 }}>Fields</th>
              </tr>
            </thead>
            <tbody>
              {filteredCatalog.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted small py-4">
                    No objects match &quot;{searchQuery.trim()}&quot;.
                  </td>
                </tr>
              ) : (
                filteredCatalog.map((row) => {
                const draft = drafts[row.object_name] || {};
                const dirty = isRowDirty(row, draft);
                return (
                  <tr key={row.object_name} className={dirty ? 'table-warning' : undefined}>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={draft.included === true}
                        disabled={disabled || saving}
                        onChange={(e) => handleIncludeToggle(row, e.target.checked)}
                        aria-label={`Include ${row.object_name}`}
                      />
                    </td>
                    <td>
                      <div className="fw-semibold">{row.object_label}</div>
                      <code className="small">{row.object_name}</code>
                      {isStandard ? (
                        <Badge bg="secondary" className="ms-2">Standard</Badge>
                      ) : row.is_custom_object ? (
                        <Badge bg="info" className="ms-2">Custom</Badge>
                      ) : (
                        <Badge bg="secondary" className="ms-2">Custom fields</Badge>
                      )}
                      {row.fields_out_of_sync && draft.included && (
                        <Badge bg="warning" text="dark" className="ms-1">Fields changed</Badge>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        disabled={disabled || saving || draft.included !== true}
                        placeholder={row.default_description || 'Description for AI'}
                        value={draft.description ?? ''}
                        onChange={(e) => handleDescriptionChange(row.object_name, e.target.value)}
                      />
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={disabled || saving || draft.included !== true}
                        onClick={() => openFieldModal(row)}
                        title="Field descriptions"
                      >
                        <FaList />
                      </Button>
                    </td>
                  </tr>
                );
              })
              )}
            </tbody>
          </Table>
        </div>
      )}

      <AiSchemaFieldNotesModal
        show={Boolean(fieldModalObject)}
        objectEntry={fieldModalObject}
        saving={false}
        onHide={() => setFieldModalObject(null)}
        onSave={handleFieldNotesSave}
      />
    </div>
  );
};

export default AiSchemaCatalogSection;
